import { cn } from '@ttab/elephant-ui/utils'
import type { Block } from '@ttab/elephant-api/newsdoc'
import { Alert, AlertDescription } from '@ttab/elephant-ui'
import { MessageCircleMoreIcon, Trash2Icon, TextIcon } from '@ttab/elephant-ui/icons'
import { TextBox } from '@/components/ui'
import type { DefaultValueOption } from '@/types/index'
import { useState, type JSX } from 'react'
import { Prompt } from '@/components/Prompt'
import type { YDocument } from '@/modules/yjs/hooks'
import { useYValue } from '@/modules/yjs/hooks'
import { isEqualDeep } from '@/modules/yjs/lib/isEqualDeep'
import { fromYStructure, getValueByYPath } from '@/shared/yUtils'
import type * as Y from 'yjs'
import { useTranslation } from 'react-i18next'
import type { TranslationKey } from '@/types/i18next.d'

/**
 * A stable key per Y structure instance.
 *
 * A note written before notes carried an id is keyed by its position, so when
 * the list shrinks React reuses the row and hands it the next note's Y.XmlText.
 * The editor binds its text once, when it mounts - Textbit passes the value to
 * Slate as `initialValue` - so without a new key the row keeps rendering the
 * text it was mounted with. Removing the first of two notes then leaves the
 * first row showing its old text while the document holds only the second, and
 * reads as though the wrong note was removed.
 *
 * Keying the editor on the identity of the text it is bound to remounts it when
 * and only when that text changes under it. Notes that carry an id are keyed by
 * it and move together with their own text, so this only covers the older ones.
 */
const bindingKeys = new WeakMap<object, string>()
let nextBindingKey = 0

function bindingKey(value: object | undefined): string {
  if (!value) {
    return 'unbound'
  }

  let key = bindingKeys.get(value)

  if (!key) {
    key = `y${++nextBindingKey}`
    bindingKeys.set(value, key)
  }

  return key
}

const Note = ({ ydoc, noteIndex, onRequestRemove, readOnly }: {
  ydoc: YDocument<Y.Map<unknown>>
  noteIndex: number
  onRequestRemove: () => void
  readOnly?: boolean
}): JSX.Element => {
  const [role, setRole] = useYValue<string>(ydoc.ele, `meta.core/note[${noteIndex}].role`)
  const [value] = useYValue<Y.XmlText>(ydoc.ele, `meta.core/note[${noteIndex}].data.text`, true)
  const [showVerifyChange, setShowVerifyChange] = useState(false)
  const { t } = useTranslation(['views', 'metaSheet'])

  const iconProps = {
    strokeWidth: 1.75,
    size: 18
  }


  const roles: DefaultValueOption[] = [
    { value: 'public', label: t('metaSheet:dropDownMenuItems.infoToCustomer'), icon: TextIcon, iconProps },
    { value: 'internal', label: t('metaSheet:dropDownMenuItems.internalNote'), icon: MessageCircleMoreIcon, iconProps }
  ]

  const selectedOptions = roles.filter((r) => r.value === role)
  const SelectedIcon = selectedOptions.length && selectedOptions[0].icon

  return (
    <Alert className={cn('flex p-1 pl-4', role === 'public'
      ? 'bg-blue-50 dark:bg-blue-800'
      : 'bg-yellow-50 dark:bg-yellow-800')}
    >
      <div className='flex flex-row w-full justify-between items-center'>
        <AlertDescription className='flex space-x-2 items-center w-full'>
          {selectedOptions?.[0] && SelectedIcon && (
            <div className='flex pr-2'>
              <SelectedIcon {...selectedOptions[0].iconProps} />
            </div>
          )}
          <TextBox
            key={`${role}-${bindingKey(value)}`}
            ydoc={ydoc}
            value={value}
            placeholder={t(`editor:addRoleInfo.${role === 'public' ? 'editorial' : 'internal'}` as TranslationKey)}
            className='font-thin text-sm whitespace-pre-wrap break-words'
            singleLine={true}
            disabled={readOnly}
          />
        </AlertDescription>
        {!readOnly && (
          <div className='hover:cursor-pointer rounded-md p-1 hover:bg-accent2' onClick={onRequestRemove}>
            <Trash2Icon size={18} strokeWidth={1.75} />
          </div>
        )}
      </div>


      {showVerifyChange && (
        <Prompt
          title={`${t('editor:changeType')}?`}
          description={t('editor:changeTypeConfirm')}
          secondaryLabel={t('common:actions.abort')}
          primaryLabel={t('editor:changeType')}
          onPrimary={() => {
            setShowVerifyChange(false)
            setRole(role === 'public' ? 'internal' : 'public')
          }}
          onSecondary={() => {
            setShowVerifyChange(false)
          }}
        />
      )}
    </Alert>
  )
}

export const Notes = ({ ydoc, readOnly }: {
  ydoc: YDocument<Y.Map<unknown>>
  readOnly?: boolean
}): JSX.Element | null => {
  const [notes] = useYValue<Block[] | undefined>(ydoc.ele, 'meta.core/note')
  const { t } = useTranslation(['common', 'editor'])

  // What the user asked to remove, captured when they asked. The list owns this
  // rather than the row: a row without an id is keyed by position, so it is
  // reused when the list changes and any state living in it would carry over to
  // whichever note has taken that position.
  const [pending, setPending] = useState<{ index: number, note: Block } | undefined>()

  if (!notes?.length) {
    return null
  }

  // Remove what the user was shown, not whatever now sits where it sat.
  const handleRemove = () => {
    const [yNotes] = getValueByYPath<Y.Array<unknown>>(ydoc.ele, 'meta.core/note', true)

    if (!pending || !yNotes?.length) {
      setPending(undefined)
      return
    }

    // Match against the document as it is now, not the render the dialog was
    // opened from. Someone else may have added or removed notes since.
    const index = indexOfNote(fromYStructure<Block[]>(yNotes), pending)

    if (index > -1) {
      // Delete the element rather than writing a new list. Writing the whole
      // core/note value is last writer wins, so it would undo a concurrent
      // removal and discard the text anyone else is typing in the survivors.
      yNotes.delete(index, 1)
    }

    setPending(undefined)
  }

  return (
    <div className='flex flex-col gap-2 sticky p-4'>
      {notes.map((note, index) => (
        <Note
          ydoc={ydoc}
          key={note.id || `index-${index}`}
          noteIndex={index}
          onRequestRemove={() => setPending({ index, note })}
          readOnly={readOnly}
        />
      ))}

      {!!pending && (
        <Prompt
          title={`${t('common:actions.remove')}?`}
          description={t('editor:removeInfoConfirm')}
          secondaryLabel={t('common:actions.abort')}
          primaryLabel={t('common:actions.remove')}
          onPrimary={handleRemove}
          onSecondary={() => {
            setPending(undefined)
          }}
        />
      )}
    </div>
  )
}

/**
 * Where the note the user asked to remove sits right now.
 *
 * A note created since ELELOG-528 carries an id, which holds even when the list
 * shifts under an open dialog. Older notes have none, so they fall back to the
 * position that was clicked, which keeps two identically worded notes apart,
 * and then to a match on content. A note that someone else has already removed
 * matches nothing, and so removes nothing rather than a bystander.
 */
function indexOfNote(notes: Block[], pending: { index: number, note: Block }): number {
  if (pending.note.id) {
    return notes.findIndex((note) => note.id === pending.note.id)
  }

  return isEqualDeep(notes[pending.index], pending.note)
    ? pending.index
    : notes.findIndex((note) => isEqualDeep(note, pending.note))
}
